// VERA learning how each person works (Eva, 3 Nov 2026): the pages and Home cards they use, what they talk about with VERA
// or clear from their three things, whether they talk or type, and when they work. Kept per person and synced with their
// settings (history.ts), so it follows them to every device. From it come:
//  - two scores for the Operations card: how they use the platform, and how well VERA knows them;
//  - what VERA learned (short lines, each removable), which also goes into VERA's answers (the server reads "notes");
//  - small changes VERA makes on her own (hiding a Home card they never use), each listed with Undo.
// VERANeural sees only the scores per company, never any of this. Patterns in records and emails VERA keeps herself,
// in her notebook (Settings → What VERA remembers).

import { useEffect, useState } from "react";
import { settingsChanged } from "./history";
import { readHomePrefs, saveHomePrefs, type HomeCard } from "./homePrefs";

export type Change = { id: string; at: number; kind: "hide-card"; card: HomeCard; text: string; undone?: boolean; seen?: boolean };
export type Learned = { id: string; text: string; note: string };
export type BehaviorData = {
  since: number; days: string[]; homeDays: string[];
  areas: Record<string, number>; cards: Record<string, number>;
  three: { talked: number; cleared: number; talkedAbout: string[] };
  vera: { talk: number; type: number; hours: number[] };
  removed: string[]; changes: Change[];
};

export const BEHAVIOR_CHANGED = "vera-behavior";
/** Home cards VERA may hide on her own when they're never used. The day's own cards (three things, calendar…) stay. */
export const MAY_HIDE: HomeCard[] = ["records", "open", "changes", "online", "operations", "teams"];
const CARD_NAMES: Partial<Record<HomeCard, string>> = { records: "Your records", open: "Open work", changes: "Latest changes", online: "Who's online", operations: "Operations overview", teams: "Teams" };
/** Home must have been opened on this many days before VERA hides anything. */
export const DAYS_BEFORE_CHANGING = 7;

let userId = "";
export function setBehaviorUser(id: string) { userId = id; }
const key = () => `vera-behavior-${userId}`;
const today = (now = Date.now()) => new Date(now).toISOString().slice(0, 10);

export const blank = (now = Date.now()): BehaviorData => ({
  since: now, days: [], homeDays: [], areas: {}, cards: {}, three: { talked: 0, cleared: 0, talkedAbout: [] },
  vera: { talk: 0, type: 0, hours: Array(24).fill(0) }, removed: [], changes: [],
});

export function readBehavior(): BehaviorData {
  try {
    const v = JSON.parse(localStorage.getItem(key()) ?? "null");
    if (!v || typeof v !== "object") return blank();
    const b = blank(v.since ?? Date.now());
    return { ...b, ...v, three: { ...b.three, ...v.three }, vera: { ...b.vera, ...v.vera, hours: Array.isArray(v.vera?.hours) && v.vera.hours.length === 24 ? v.vera.hours : b.vera.hours } };
  } catch { return blank(); }
}

let lastSync = 0;
function save(b: BehaviorData, sync = false) {
  if (!userId) return;
  const kept: BehaviorData = { ...b, days: b.days.slice(-60), homeDays: b.homeDays.slice(-30), three: { ...b.three, talkedAbout: b.three.talkedAbout.slice(-10) }, changes: b.changes.slice(-30) };
  const s = scores(kept);
  // The server reads usage, knows, changes and notes (VERA's answers, VERANeural's totals); the rest is for this card.
  const out = { ...kept, usage: s.usage, knows: s.knows, changesMade: kept.changes.filter((c) => !c.undone).length, notes: learned(kept).map((l) => l.note) };
  try { localStorage.setItem(key(), JSON.stringify({ ...out, changes: kept.changes })); } catch { /* private window: this visit only */ }
  window.dispatchEvent(new Event(BEHAVIOR_CHANGED));
  // Sent with the person's settings, at most every few minutes unless something they decided changed.
  if (sync || Date.now() - lastSync > 5 * 60_000) { lastSync = Date.now(); settingsChanged(); }
}

function touch(b: BehaviorData, now = Date.now()): BehaviorData {
  const d = today(now);
  return b.days[b.days.length - 1] === d ? b : { ...b, days: [...b.days, d] };
}

export function recordArea(area: string) {
  const b = touch(readBehavior());
  const homeDays = area === "home" && b.homeDays[b.homeDays.length - 1] !== today() ? [...b.homeDays, today()] : b.homeDays;
  save({ ...b, homeDays, areas: { ...b.areas, [area]: (b.areas[area] ?? 0) + 1 } });
}
export function recordCard(card: string) {
  const b = readBehavior();
  save({ ...b, cards: { ...b.cards, [card]: (b.cards[card] ?? 0) + 1 } });
}
export function recordThree(how: "talked" | "cleared", text: string) {
  const b = touch(readBehavior());
  save({ ...b, three: { ...b.three, [how]: b.three[how] + 1, talkedAbout: how === "talked" ? [...b.three.talkedAbout, text.slice(0, 80)] : b.three.talkedAbout } });
}
export function recordVera(how: "talk" | "type", now = Date.now()) {
  const b = touch(readBehavior(), now);
  const hours = [...b.vera.hours]; hours[new Date(now).getHours()]++;
  save({ ...b, vera: { ...b.vera, [how]: b.vera[how] + 1, hours } });
}

const days14 = (b: BehaviorData, now = Date.now()) => b.days.filter((d) => d >= today(now - 13 * 86_400_000)).length;
const handled = (b: BehaviorData) => b.three.talked + b.three.cleared;
const visits = (b: BehaviorData) => Object.values(b.areas).reduce((a, n) => a + n, 0);

/** How they use the platform (active days, how much of it, things handled) and how well VERA knows them, 0–100 each. */
export function scores(b: BehaviorData, now = Date.now()) {
  const areasUsed = Object.values(b.areas).filter((n) => n > 0).length;
  const usage = Math.round(Math.min(days14(b, now), 10) / 10 * 50 + Math.min(areasUsed, 6) / 6 * 25 + Math.min(handled(b), 10) / 10 * 25);
  const veraTotal = b.vera.talk + b.vera.type;
  const signals = [b.days.length >= 5, visits(b) >= 30, veraTotal >= 10, handled(b) >= 5, learned(b).length >= 3];
  const knows = Math.round(signals.filter(Boolean).length / signals.length * 100);
  return { usage, knows, activeDays: days14(b, now), areasUsed, handled: handled(b), veraTotal };
}

const AREA_NAMES: Record<string, string> = { home: "Home", workspace: "Workspace", records: "Records", field: "VERA Field", activity: "Activity", files: "Files", connections: "Connections", operations: "Operations", settings: "Settings", workforce: "Workforce", workflows: "Work processes", proposals: "Proposals", planner: "Planner" };

/** What VERA learned from how they work: shown on the card ("text") and sent to VERA ("note"). Removed ones stay out. */
export function learned(b: BehaviorData): Learned[] {
  const out: Learned[] = [];
  const top = Object.entries(b.areas).filter(([a]) => a !== "home").sort((x, y) => y[1] - x[1]).slice(0, 2).map(([a]) => AREA_NAMES[a] ?? a);
  if (visits(b) >= 10 && top.length) out.push({ id: "areas", text: `You work mostly in ${top.join(" and ")}.`, note: `Works mostly in ${top.join(" and ")}.` });
  const veraTotal = b.vera.talk + b.vera.type;
  if (veraTotal >= 5) {
    let best = 0, at = 0;
    for (let h = 0; h < 24; h++) { const n = b.vera.hours[h] + b.vera.hours[(h + 1) % 24] + b.vera.hours[(h + 2) % 24]; if (n > best) { best = n; at = h; } }
    if (best / veraTotal >= 0.5) out.push({ id: "hours", text: `You usually work with VERA between ${at}:00 and ${(at + 3) % 24}:00.`, note: `Usually works with VERA between ${at}:00 and ${(at + 3) % 24}:00 (their time).` });
    if (b.vera.talk / veraTotal >= 0.65) out.push({ id: "talk", text: "You'd rather talk to VERA than type.", note: "Prefers talking to VERA over typing: keep answers easy to listen to." });
    else if (b.vera.type / veraTotal >= 0.8) out.push({ id: "talk", text: "You'd rather type to VERA than talk.", note: "Prefers typing to VERA." });
  }
  if (b.three.cleared >= 3 && b.three.cleared > b.three.talked * 2) out.push({ id: "three-clear", text: "You clear most suggested things quickly: VERA keeps them few, and only what really matters.", note: "Clears most suggested things quickly: suggest fewer, only what really matters." });
  if (b.three.talked >= 3) out.push({ id: "three-talk", text: "You like to talk things through with VERA before acting.", note: "Likes to talk things through before acting: think it through with them, then propose the step." });
  const recent = [...new Set(b.three.talkedAbout.slice(-3).reverse())];
  if (recent.length) out.push({ id: "topics", text: `Lately you talked with VERA about: ${recent.join("; ")}.`, note: `Recently talked through: ${recent.join("; ")}.` });
  return out.filter((l) => !b.removed.includes(l.id));
}

export function removeLearned(id: string) { const b = readBehavior(); save({ ...b, removed: [...new Set([...b.removed, id])] }, true); }

/** What VERA could change next, said as a suggestion (bigger things she doesn't do on her own). */
export function suggestions(b: BehaviorData, menu: string[]): string[] {
  if (b.days.length < DAYS_BEFORE_CHANGING) return [];
  const unused = menu.filter((a) => !["home", "settings", "workspace"].includes(a) && !b.areas[a]).map((a) => AREA_NAMES[a] ?? a);
  return unused.length ? [`You haven't opened ${unused.slice(0, 3).join(", ")} yet. Want VERA to show you what they're for, or move them out of the way?`] : [];
}

/** The small change VERA makes on her own today, if any: hide one Home card that's never used. One a day, never twice. */
export function nextChange(b: BehaviorData, hidden: HomeCard[], now = Date.now()): Change | null {
  if (b.homeDays.length < DAYS_BEFORE_CHANGING) return null;
  if (b.changes.some((c) => today(c.at) === today(now))) return null;
  const card = MAY_HIDE.find((c) => !hidden.includes(c) && !(b.cards[c] ?? 0) && !b.changes.some((x) => x.card === c));
  return card ? { id: `c${now}`, at: now, kind: "hide-card", card, text: `VERA hid "${CARD_NAMES[card]}" on Home: you haven't used it in ${b.homeDays.length} days.` } : null;
}

/** Runs once when Studio opens: makes today's small change, if there is one. */
export function makeChanges(now = Date.now()): Change | null {
  const b = readBehavior();
  const prefs = readHomePrefs();
  const c = nextChange(b, prefs.hidden, now);
  if (!c) return null;
  saveHomePrefs({ ...prefs, hidden: [...new Set([...prefs.hidden, c.card])] });
  save({ ...b, changes: [...b.changes, c] }, true);
  return c;
}

export function undoChange(id: string) {
  const b = readBehavior();
  const c = b.changes.find((x) => x.id === id);
  if (!c || c.undone) return;
  const prefs = readHomePrefs();
  saveHomePrefs({ ...prefs, hidden: prefs.hidden.filter((h) => h !== c.card) });
  save({ ...b, changes: b.changes.map((x) => (x.id === id ? { ...x, undone: true, seen: true } : x)) }, true);
}
export function seeChanges() { const b = readBehavior(); save({ ...b, changes: b.changes.map((x) => ({ ...x, seen: true })) }); }

export function useBehavior(): BehaviorData {
  const [b, setB] = useState(readBehavior);
  useEffect(() => {
    const on = () => setB(readBehavior());
    on();
    window.addEventListener(BEHAVIOR_CHANGED, on);
    return () => window.removeEventListener(BEHAVIOR_CHANGED, on);
  }, []);
  return b;
}
